import React from "react";
import { Box, Grid, Typography, Container, Paper } from "@mui/material";
import adidas from "../../assets/about_us/adidas.png";
import ali_pay from "../../assets/about_us/ali_pay.png";
import discover from "../../assets/about_us/discover.png";
import hipercard from "../../assets/about_us/hipercard.png";
import jcb from "../../assets/about_us/jcb.png";
import { styled } from "@mui/material/styles";
import { CheckCircle } from "@mui/icons-material";

const CustomImage = styled("img")(({ theme }) => ({
  objectFit: "cover",
  width: "100%",
  height: "500px",
  borderRadius: "8px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
  transition: "transform 0.4s ease, box-shadow 0.4s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 30px 80px rgba(2, 119, 189, 0.25)",
  },
  "@media screen and (max-width: 900px)": {
    height: "350px",
  },
  "@media screen and (max-width: 600px)": {
    height: "250px",
  },
}));

const PartnerLogo = styled("img")(({ theme }) => ({
  objectFit: "contain",
  width: "140px",
  height: "90px",
  margin: "10px",
  padding: "15px",
  background: "white",
  borderRadius: "8px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s ease",
  filter: "grayscale(100%)",
  opacity: 0.7,
  "&:hover": {
    filter: "grayscale(0%)",
    opacity: 1,
    transform: "translateY(-8px) scale(1.05)",
    boxShadow: "0 16px 40px rgba(2, 119, 189, 0.2)",
  },
  "@media screen and (max-width: 600px)": {
    width: "100px",
    height: "70px",
    margin: "8px",
  },
}));

const FeatureItem = ({ text }) => (
  <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
    <CheckCircle sx={{ color: "#0277BD", marginRight: "12px", fontSize: "24px" }} />
    <Typography
      fontFamily="Montserrat"
      fontSize={{ xs: "14px", md: "16px" }}
      fontWeight={500}
      color="#4a5568"
    >
      {text}
    </Typography>
  </Box>
);

function AboutUs() {
  return (
    <Box 
      id="about_us"
      sx={{
        padding: { xs: "40px 0", md: "80px 0" },
        background: "linear-gradient(180deg, #ffffff 0%, #f5f7fa 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-30%",
          left: "-20%",
          width: "50%",
          height: "100%",
          background: "radial-gradient(circle, rgba(2, 119, 189, 0.06) 0%, transparent 70%)",
          borderRadius: "50%",
        }
      }}
    >
      <Container maxWidth="xl">
        <Grid
          container
          direction="column"
          sx={{ position: "relative", zIndex: 1 }}
        >
          {/* Header */}
          <Grid item container rowSpacing={3} sx={{ marginBottom: "50px" }}>
            <Grid item xs={12}>
              <Box
                sx={{
                  width: { xs: "60px", md: "80px" },
                  height: "4px",
                  background: "linear-gradient(90deg, #03A9F4 0%, #0277BD 100%)",
                  borderRadius: "2px",
                  marginBottom: "16px",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                component="div"
                fontFamily="Montserrat"
                fontSize={{ xs: "32px", md: "52px" }}
                fontWeight={900}
                lineHeight={1.2}
                letterSpacing="0.02em"
                textTransform="uppercase"
                className="fade-in-up"
                sx={{
                  background: "linear-gradient(135deg, #03A9F4 0%, #0277BD 50%, #025D9E 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                About Us
              </Typography>
            </Grid>
          </Grid>

          {/* Content Section */}
          <Grid item container spacing={6} alignItems="center" sx={{ marginBottom: "60px" }}>
            <Grid item xs={12} md={6} className="slide-in-left">
              <Paper
                elevation={0}
                sx={{
                  padding: { xs: "30px", md: "40px" },
                  background: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "12px",
                  border: "1px solid rgba(2, 119, 189, 0.1)",
                }}
              >
                <Typography
                  component="div"
                  fontFamily="Montserrat"
                  fontSize={{ xs: "16px", md: "18px" }}
                  fontWeight={500}
                  lineHeight={1.8}
                  color="#2d3748"
                  marginBottom="30px"
                >
                  RCS Worldwide Express (Pvt.) Ltd. is a significant player in
                  express courier services in Pakistan. The name is synonymous with
                  premium quality in major financial exchanges, banks, institutions,
                  trading companies and more. We offer a wide range of express
                  delivery solutions that map your business needs.
                </Typography>

                <Box sx={{ marginTop: "30px" }}>
                  <FeatureItem text="Premium quality courier services" />
                  <FeatureItem text="Trusted by major institutions" />
                  <FeatureItem text="Wide range of delivery solutions" />
                  <FeatureItem text="Domestic & International coverage" />
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6} className="slide-in-right">
              <CustomImage src="/about-us.jpeg" alt="About RCS Express" />
            </Grid>
          </Grid>

          {/* Partners Section */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", marginBottom: "40px" }}>
              <Typography
                fontFamily="Montserrat"
                fontSize={{ xs: "20px", md: "28px" }}
                fontWeight={700}
                color="#1a1a2e"
                marginBottom="12px"
              >
                Trusted by Leading Brands
              </Typography>
              <Typography
                fontFamily="Montserrat"
                fontSize={{ xs: "14px", md: "16px" }}
                fontWeight={500}
                color="#4a5568"
              >
                Partners who trust our services
              </Typography>
            </Box>

            <Grid
              container
              item
              justifyContent="center"
              alignItems="center"
              sx={{ 
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: "10px", md: "20px" }
              }}
            >
              <PartnerLogo src={ali_pay} alt="Alipay" />
              <PartnerLogo src={discover} alt="Discover" />
              <PartnerLogo src={adidas} alt="Adidas" />
              <PartnerLogo src={hipercard} alt="Hipercard" />
              <PartnerLogo src={jcb} alt="JCB" />
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default AboutUs;
